import { AlertTriangle, ChevronRight, FileText, Trash2 } from "lucide-react";
import { ConfirmButton } from "@/components/ConfirmButton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatDate } from "@/lib/api";

const STATO = {
  attivo: { label: "Attivo", cls: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
  in_scadenza: { label: "In scadenza", cls: "bg-amber-50 text-amber-800 ring-amber-200" },
  scaduto: { label: "Scaduto", cls: "bg-rose-50 text-rose-700 ring-rose-200" },
  cessato: { label: "Cessato", cls: "bg-slate-100 text-slate-600 ring-slate-200" },
  nessuno: { label: "Nessun contratto", cls: "bg-slate-50 text-slate-500 ring-slate-200 border-dashed" },
};

export const StatoBadge = ({ stato }) => {
  const s = STATO[stato] || STATO.nessuno;
  return <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${s.cls}`} data-testid={`stato-badge-${stato}`}>{s.label}</span>;
};

export const TIPO_COM = { assunzione: "Assunzione", proroga: "Proroga", trasformazione: "Trasformazione", cessazione: "Cessazione" };

export const HoursConflictBadge = ({ conflict, testId }) => conflict ? (
  <TooltipProvider delayDuration={100}><Tooltip>
    <TooltipTrigger asChild>
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800 ring-1 ring-inset ring-amber-200" data-testid={testId}>
        <AlertTriangle className="h-3 w-3" /> UNILAV {conflict.unilav} h
      </span>
    </TooltipTrigger>
    <TooltipContent className="max-w-xs text-xs">Ore diverse tra i documenti: contratto {conflict.contratto} h/sett., UNILAV {conflict.unilav} h/sett. Verifica quale sia corretto.</TooltipContent>
  </Tooltip></TooltipProvider>
) : null;

export const ContractsTable = ({ rows, onOpen, onDelete }) => (
  <div className="panel overflow-x-auto" data-testid="contracts-table">
    <Table className="min-w-[900px]">
      <TableHeader>
        <TableRow>
          <TableHead>Dipendente</TableHead><TableHead>Contratto</TableHead><TableHead>Mansione</TableHead>
          <TableHead className="text-right">Ore/sett.</TableHead><TableHead>Inizio</TableHead><TableHead>Fine</TableHead>
          <TableHead>Stato</TableHead><TableHead className="text-right">Documenti</TableHead><TableHead className="w-12" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => {
          const c = r.current;
          return (
            <TableRow key={r.employee_id} className="cursor-pointer hover:bg-slate-50" onClick={() => onOpen(r)} data-testid={`contract-row-${r.employee_id}`}>
              <TableCell>
                <p className="font-medium text-slate-900">{r.cognome} {r.nome}</p>
                <p className="font-mono text-[11px] text-slate-500">{r.codice_fiscale || "—"}{!r.attivo && " · cessato"}</p>
              </TableCell>
              <TableCell className="text-sm">
                {c ? <><p>{c.tipologia_contratto || "—"}</p><p className="text-xs text-slate-500">{TIPO_COM[c.tipo_comunicazione] || ""}{c.livello ? ` · ${c.livello}` : ""}</p></> : <span className="text-slate-400">—</span>}
              </TableCell>
              <TableCell className="text-sm">{c?.mansione || <span className="text-slate-400">—</span>}</TableCell>
              <TableCell className="text-right font-mono text-sm">
                <div className="flex flex-col items-end gap-1">{c?.ore_settimanali ?? "—"}<HoursConflictBadge conflict={r.ore_conflitto} testId={`hours-conflict-${r.employee_id}`} /></div>
              </TableCell>
              <TableCell className="text-sm text-slate-600">{c?.data_inizio ? formatDate(c.data_inizio, false) : "—"}</TableCell>
              <TableCell className="text-sm text-slate-600">{c?.data_fine ? formatDate(c.data_fine, false) : c ? "Indeterminato" : "—"}</TableCell>
              <TableCell><StatoBadge stato={r.stato} /></TableCell>
              <TableCell className="text-right">
                <span className="inline-flex items-center gap-1 text-sm text-slate-600"><FileText className="h-4 w-4" /> {r.count} <ChevronRight className="h-4 w-4 text-slate-400" /></span>
              </TableCell>
              <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                <ConfirmButton testId={`delete-employee-${r.employee_id}`} title={`Eliminare ${r.cognome} ${r.nome}?`}
                  description={`Il dipendente verrà rimosso dall'anagrafica insieme ai suoi ${r.count} documenti (contratti e UNILAV). L'operazione non è reversibile.`}
                  confirmLabel="Elimina" onConfirm={() => onDelete(r)} className="text-slate-500 hover:text-red-600">
                  <Trash2 className="h-4 w-4" />
                </ConfirmButton>
              </TableCell>
            </TableRow>
          );
        })}
        {rows.length === 0 && (
          <TableRow><TableCell colSpan={9} className="py-10 text-center text-sm text-slate-500" data-testid="contracts-empty">Nessun dipendente in questo filtro.</TableCell></TableRow>
        )}
      </TableBody>
    </Table>
  </div>
);
